import { useRef } from "react";
import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  MailTestCatalogDto,
  MailTestSamplesDto,
  MailTestSendResultDto,
  MailTestTemplateDto,
  MailTestUsedSamplesDto,
  MailTestVariantDto,
} from "../models/responses/MailTestDto";
import { MailTestSendRequest } from "../models/bodies/MailTestSendRequest";

// Admin mail test (flag REACT_APP_MAIL_TEST_ENABLED). Admin surface with a
// fixed call budget: the template catalogue is ONE GET on page open (5 min
// stale, no polling, refetch only via the Refresh button), a vendor's
// samples are ONE GET the first time that vendor is chosen (kept for 5 min,
// so switching template/variant/sample costs nothing), and "Preview data" /
// "Send test mail" are ONE POST per click. Both queries run with retry:
// false - the app's QueryClient sets no retry, so react-query's default (3
// retries with back-off) would turn a 404 (API flag off) or a 5xx into FOUR
// calls and a ~7 s wait before the screen can say what happened. The send
// mutation invalidates NOTHING: a test mail changes no data anywhere, so
// there is nothing to re-read.
export interface MailTestRepository {
  useTemplates(options?: { enabled?: boolean }): Resource<MailTestCatalogDto>;
  // Idle until a vendor is chosen (vendorOrgId defined); never refetches on
  // its own afterwards.
  useSamples(
    vendorOrgId: number | undefined,
    options?: { take?: number; enabled?: boolean },
  ): Resource<MailTestSamplesDto>;
  // The Refresh button: re-reads the catalogue and the chosen vendor's
  // samples (one call each) - the samples of other vendors are dropped from
  // the cache so a later pick reads fresh data.
  refresh(vendorOrgId?: number): Promise<void>;
  // One hook instance per screen: see MailTestSend.
  useSend(): MailTestSend;
}

export interface MailTestSend {
  // ONE POST ops/mail-test/send; resolves with the normalised answer. A
  // call while one is in flight returns the running promise - no second
  // POST (and no second test mail against the rate limit), whatever the
  // button state was when the click landed.
  send(body: MailTestSendRequest): Promise<MailTestSendResultDto>;
  isLoading: boolean;
  // Any send on the screen (Preview and Send share one mutation key).
  isAnyRunning: boolean;
  // The last answer of this hook (cleared when a new run starts).
  data: MailTestSendResultDto | undefined;
  error: unknown;
  // What the running/last call was (dryRun or a real send), so the screen
  // can label the busy state and the result.
  variables: MailTestSendRequest | undefined;
  reset(): void;
}

const text = (value: unknown): string | null =>
  value === null || value === undefined ? null : String(value);

const flag = (value: unknown): boolean => value === true;

const int = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const ints = (value: unknown): number[] =>
  Array.isArray(value)
    ? value.filter(
        (v): v is number => typeof v === "number" && Number.isFinite(v),
      )
    : [];

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .filter((v) => v !== null && v !== undefined)
        .map((v) => String(v))
    : [];

const record = <T,>(
  value: unknown,
  keep: (v: unknown) => v is T,
): Record<string, T> => {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  const out: Record<string, T> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (keep(v)) out[k] = v;
  }
  return out;
};

const isBoolean = (v: unknown): v is boolean => typeof v === "boolean";
const isFiniteNumber = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

// { key: value } when the value is a non-empty string / a finite number,
// {} otherwise - spread into an object to add a field only when present.
const optionalText = (key: string, value: unknown): Record<string, string> => {
  const s = text(value);
  return s === null || s === "" ? {} : { [key]: s };
};

const optionalInt = (key: string, value: unknown): Record<string, number> => {
  const n = int(value);
  return n === null ? {} : { [key]: n };
};

function normalizeUsedSamples(raw: unknown): MailTestUsedSamplesDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  return {
    vendorOrgId: int(body.vendorOrgId),
    customerOrgId: int(body.customerOrgId),
    logoIds: ints(body.logoIds),
    designIds: ints(body.designIds),
    orderId: int(body.orderId),
    userId: int(body.userId),
    entryId: int(body.entryId),
  };
}

// The raw JSON of POST ops/mail-test/send made safe for the screen: every
// string nullable, arrays present (missing -> []), booleans false unless
// true, `data` kept as the object the API sent (it IS the template data and
// is shown verbatim) or null. A body that is not an object counts as empty.
export function normalizeMailTestSendResult(
  raw: unknown,
): MailTestSendResultDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  const data =
    body.data !== null &&
    typeof body.data === "object" &&
    !Array.isArray(body.data)
      ? (body.data as Record<string, unknown>)
      : null;
  return {
    sent: flag(body.sent),
    dryRun: flag(body.dryRun),
    templateKey: text(body.templateKey) ?? "",
    variant: text(body.variant),
    resolvedVariant: text(body.resolvedVariant),
    kind: text(body.kind) ?? "",
    templateSetting: text(body.templateSetting),
    templateId: text(body.templateId),
    configured: flag(body.configured),
    senderName: text(body.senderName),
    replyTo: text(body.replyTo),
    replyToRule: text(body.replyToRule),
    recipient: text(body.recipient) ?? "",
    realRecipientRule: text(body.realRecipientRule),
    data,
    subject: text(body.subject),
    html: text(body.html),
    categories: strings(body.categories),
    samples: normalizeUsedSamples(body.samples),
    flags: record(body.flags, isBoolean),
    warnings: strings(body.warnings),
    sendGridStatus: int(body.sendGridStatus),
    sendGridError: text(body.sendGridError),
    callBudget:
      body.callBudget === null || body.callBudget === undefined
        ? null
        : record(body.callBudget, isFiniteNumber),
    // feat/mail-i18n: added ONLY when the API sent them, so an answer from
    // an API without the feature normalises to exactly the object above.
    ...optionalText("lang", body.lang),
    ...optionalText("langSource", body.langSource),
    ...optionalInt("tKeyCount", body.tKeyCount),
  };
}

// The catalogue as the screen reads it: templates an array of templates
// whose variants are arrays (missing -> []) with `requires` an array of
// strings. Anything that is not a template object is dropped.
export function normalizeMailTestCatalog(raw: unknown): MailTestCatalogDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  const templates: MailTestTemplateDto[] = (
    Array.isArray(body.templates) ? body.templates : []
  )
    .filter((t) => t !== null && typeof t === "object")
    .map((t) => {
      const r = t as Record<string, unknown>;
      const variants: MailTestVariantDto[] = (
        Array.isArray(r.variants) ? r.variants : []
      )
        .filter((v) => v !== null && typeof v === "object")
        .map((v) => {
          const rv = v as Record<string, unknown>;
          return {
            id: text(rv.id) ?? "",
            description: text(rv.description) ?? "",
            sender: text(rv.sender),
            requires: strings(rv.requires),
            dead: flag(rv.dead),
          };
        })
        .filter((v) => v.id !== "");
      return {
        key: text(r.key) ?? "",
        kind: text(r.kind) ?? "template",
        description: text(r.description),
        setting: text(r.setting),
        azureSetting: text(r.azureSetting),
        configured: flag(r.configured),
        fallbackSetting: text(r.fallbackSetting),
        variants,
      };
    })
    .filter((t) => t.key !== "");
  const limits =
    body.limits !== null && typeof body.limits === "object"
      ? {
          maxSendsPerHour:
            int((body.limits as Record<string, unknown>).maxSendsPerHour) ?? 0,
          dryRunSoftCapPerHour:
            int((body.limits as Record<string, unknown>).dryRunSoftCapPerHour) ??
            0,
        }
      : null;
  const excluded = (Array.isArray(body.excluded) ? body.excluded : [])
    .filter((e) => e !== null && typeof e === "object")
    .map((e) => {
      const r = e as Record<string, unknown>;
      return { key: text(r.key) ?? "", reason: text(r.reason) ?? "" };
    });
  return {
    flags: record(body.flags, isBoolean),
    limits,
    templates,
    excluded,
    // feat/mail-i18n: only when the API sent them (an older API has neither;
    // the screen then offers the full language list and lets the API decide).
    ...(typeof body.i18nEnabled === "boolean"
      ? { i18nEnabled: body.i18nEnabled }
      : {}),
    ...(Array.isArray(body.languages)
      ? { languages: strings(body.languages) }
      : {}),
  };
}

// The samples as the screen reads them: the five lists always arrays.
export function normalizeMailTestSamples(raw: unknown): MailTestSamplesDto {
  const body = (
    raw !== null && typeof raw === "object" ? raw : {}
  ) as Record<string, unknown>;
  const list = <T,>(value: unknown): T[] =>
    Array.isArray(value)
      ? (value.filter((v) => v !== null && typeof v === "object") as T[])
      : [];
  const vendor = (
    body.vendor !== null && typeof body.vendor === "object" ? body.vendor : {}
  ) as Record<string, unknown>;
  return {
    ...(body as unknown as MailTestSamplesDto),
    vendor: {
      ...(vendor as unknown as MailTestSamplesDto["vendor"]),
      id: int(vendor.id) ?? 0,
      name: text(vendor.name) ?? "",
      navNumber: text(vendor.navNumber),
    },
    customers: list(body.customers),
    users: list(body.users),
    logos: list(body.logos),
    designs: list(body.designs),
    orders: list(body.orders),
  };
}

export const MAIL_TEST_ROOT_KEY = "mailTest";
export const MAIL_TEST_SEND_KEY = [MAIL_TEST_ROOT_KEY, "send"];
const STALE_MS = 5 * 60 * 1000;

export function useMailTestRepository(): MailTestRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useTemplates(options?: {
    enabled?: boolean;
  }): Resource<MailTestCatalogDto> {
    return useQuery(
      [MAIL_TEST_ROOT_KEY, "templates"],
      async () =>
        normalizeMailTestCatalog(await apiClient.getMailTestTemplates()),
      {
        staleTime: STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        enabled: options?.enabled ?? true,
      },
    );
  }

  function useSamples(
    vendorOrgId: number | undefined,
    options?: { take?: number; enabled?: boolean },
  ): Resource<MailTestSamplesDto> {
    return useQuery(
      [MAIL_TEST_ROOT_KEY, "samples", vendorOrgId ?? -1, options?.take ?? null],
      async () =>
        normalizeMailTestSamples(
          await apiClient.getMailTestSamples(
            vendorOrgId as number,
            options?.take,
          ),
        ),
      {
        staleTime: STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        enabled: vendorOrgId !== undefined && (options?.enabled ?? true),
      },
    );
  }

  async function refresh(vendorOrgId?: number): Promise<void> {
    // Other vendors' samples have no observer: dropped (no call now, ONE
    // call if that vendor is chosen again). The catalogue and the chosen
    // vendor's samples are refetched in place (refetchType "active").
    queryClient.removeQueries({
      queryKey: [MAIL_TEST_ROOT_KEY, "samples"],
      type: "inactive",
    });
    await Promise.all([
      queryClient.invalidateQueries([MAIL_TEST_ROOT_KEY, "templates"]),
      ...(vendorOrgId === undefined
        ? []
        : [
            queryClient.invalidateQueries([
              MAIL_TEST_ROOT_KEY,
              "samples",
              vendorOrgId,
            ]),
          ]),
    ]);
  }

  function useSend(): MailTestSend {
    const inFlight = useRef<Promise<MailTestSendResultDto> | undefined>(
      undefined,
    );
    const mutation = useMutation<
      MailTestSendResultDto,
      unknown,
      MailTestSendRequest
    >({
      mutationKey: MAIL_TEST_SEND_KEY,
      mutationFn: async (body) =>
        normalizeMailTestSendResult(await apiClient.sendMailTest(body)),
      // No onSuccess: a test mail changes nothing, nothing is invalidated.
    });
    const isAnyRunning = useIsMutating({ mutationKey: MAIL_TEST_SEND_KEY }) > 0;

    function send(body: MailTestSendRequest): Promise<MailTestSendResultDto> {
      // The ref is set synchronously: react-query flips isLoading only after
      // a microtask, so two clicks in the same tick would otherwise both
      // pass a state-based guard - and send two test mails.
      if (inFlight.current) return inFlight.current;
      const run = mutation.mutateAsync(body).finally(() => {
        inFlight.current = undefined;
      });
      inFlight.current = run;
      return run;
    }

    return {
      send,
      isLoading: mutation.isLoading,
      isAnyRunning,
      data: mutation.data,
      error: mutation.error,
      variables: mutation.variables,
      reset: mutation.reset,
    };
  }

  return { useTemplates, useSamples, refresh, useSend };
}
